import { useEnvironment } from '@/hooks/use-environment'

/**
 * Says which environment this is, on everything except production.
 *
 * The point is that its ABSENCE is informative: no badge means production. So it renders for
 * local, for dev, and for any Cloud Run service that is not production — see
 * `use-environment.ts` for why "we could not tell" is treated as production rather than
 * labelled, and why the signal comes from the server.
 *
 *
 * NON-INTERACTIVE, DELIBERATELY
 *
 * A plain `<span>` with no dismiss control, for two reasons. An indicator you can turn off
 * is one you will turn off and then forget about. And the shell's axe baseline counts
 * `target-size` nodes — every sidebar pin button is one — so a small clickable thing added
 * here would move a number that is meant to move only when the nav does.
 *
 *
 * COLOUR IS NOT THE MESSAGE
 *
 * The word LOCAL or DEV is the message; the colour only draws the eye. `bg-warning` /
 * `text-warning-foreground` are CSS variables (Pollen on Prussian, ~8:1, with a dark-mode
 * pair already defined), which also keeps the brand guardrail's hex check out of it. Pollen
 * is fill-only by brand rule — it fails as text at about 1.5:1 — so it is the background
 * here and never the foreground.
 */
export function EnvironmentBadge() {
  const { label } = useEnvironment()

  if (!label) return null

  return (
    <span
      // A live region would announce it on every page change, which is noise for something
      // that cannot change without a reload. Announced once, as part of the header.
      role="status"
      className="rounded px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide bg-warning text-warning-foreground whitespace-nowrap"
    >
      {/* Spelled out for a screen reader, short on screen: "DEV" alone is ambiguous read
          aloud in a list of header controls. */}
      <span className="sr-only">Environment: </span>
      {label}
      <span className="sr-only"> — this is not production</span>
    </span>
  )
}
